import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { msalConfig } from "../../authentication/authConfig";
import {
  IconButton,
  MessageBar,
  MessageBarType,
  Panel,
  PanelType,
  PrimaryButton,
  DefaultButton,
  TextField,
  Dropdown,
  IDropdownOption,
  DetailsRow,
  IDetailsRowProps,
} from "@fluentui/react";
import AddButton from "../../ui-controls/AddButton";
import SearchBox from "../../ui-controls/SearchBox";
import MoreActionsButton from "../../ui-controls/MoreActionsButton";
import { DataGrid, DataGridColumn } from "../../ui-controls/DataGrid";
import ShimmerList from "../../ui-controls/ShimmerList";
import {
  getRestoreCustomers,
  getRestoreJobs,
  getRestoreJobStepTypes,
  createRestoreCustomer,
  createRestoreJob,
  updateRestoreJobStep,
  updateRestoreCustomerStatus,
  getDatabases,
  searchXpressTraxCustomers,
  RestoreCustomerResponse,
  RestoreJobResponse,
  RestoreJobStepResponse,
  RestoreJobStepTypeResponse,
  DatabaseResponse,
  XpressTraxCustomerResponse,
} from "../../api/cortexCloning";

const getNextDatabaseLetter = (databases: DatabaseResponse[]): string => {
  const letters = databases
    .map((db) => db.companyLetter.toUpperCase())
    .filter((l) => /^[A-Z]$/.test(l));
  if (letters.length === 0) return "A";
  const highest = letters.reduce((a, b) => (a > b ? a : b));
  if (highest === "Z") {
    throw new Error("All database letters A–Z are already in use for this company. Contact support to clean up unused clones before creating a new one.");
  }
  return String.fromCharCode(highest.charCodeAt(0) + 1);
};

const CortexCloningPage = () => {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const [currentUserEmail, setCurrentUserEmail] = useState("");

  useEffect(() => {
    if (!account) return;
    instance
      .acquireTokenSilent({
        scopes: [msalConfig.scope],
        account,
      })
      .then((result) => {
        const claims = result.idTokenClaims as Record<string, any> | undefined;
        setCurrentUserEmail(claims?.email || "");
      })
      .catch((err) => console.error("Failed to get user email from token:", err));
  }, [account, instance]);
  const [customers, setCustomers] = useState<RestoreCustomerResponse[]>([]);
  const [jobs, setJobs] = useState<RestoreJobResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("Active");
  const [selectedCustomer, setSelectedCustomer] = useState<RestoreCustomerResponse | null>(null);
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false);
  const [visibleJobCount, setVisibleJobCount] = useState(5);

  // Delete state
  const [customerToDelete, setCustomerToDelete] = useState<RestoreCustomerResponse | null>(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");

  // Add panel fields
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [server, setServer] = useState("");
  const [databases, setDatabases] = useState<DatabaseResponse[]>([]);
  const [selectedDatabase, setSelectedDatabase] = useState("");
  const [targetDatabaseName, setTargetDatabaseName] = useState("");
  const [loadingDatabases, setLoadingDatabases] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Step types (fetched from API — avoids hardcoding IDs in clone/teardown calls)
  const [stepTypes, setStepTypes] = useState<RestoreJobStepTypeResponse[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // XpressTrax search state
  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [companySearchResults, setCompanySearchResults] = useState<XpressTraxCustomerResponse[]>([]);
  const [loadingCompanySearch, setLoadingCompanySearch] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  // Auto-refresh when jobs are in progress
  useEffect(() => {
    const hasActiveJobs = jobs.some((j) => j.status === "In Progress" || j.status === "New");
    if (!hasActiveJobs) return;
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [jobs]);

  // Auto-fetch databases when both companyId and server are set
  useEffect(() => {
    const id = parseInt(companyId);
    if (!id || !server) {
      setDatabases([]);
      setSelectedDatabase("");
      setTargetDatabaseName("");
      return;
    }

    let cancelled = false;
    const fetchDatabases = async () => {
      setLoadingDatabases(true);
      try {
        const data = await getDatabases(id, server);
        if (cancelled) return;
        setDatabases(data);
        setSelectedDatabase("");
        const nextLetter = getNextDatabaseLetter(data);
        setTargetDatabaseName(`ExpData_${id}_${nextLetter}`);
      } catch (error) {
        if (cancelled) return;
        setErrorMessage("Failed to look up databases for this company/server");
        setDatabases([]);
        setTargetDatabaseName("");
      } finally {
        if (!cancelled) setLoadingDatabases(false);
      }
    };

    fetchDatabases();
    return () => { cancelled = true; };
  }, [companyId, server]);

  // Debounced XpressTrax company search
  useEffect(() => {
    if (companySearchTerm.length < 2) {
      setCompanySearchResults([]);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoadingCompanySearch(true);
      try {
        const result = await searchXpressTraxCustomers(companySearchTerm);
        if (!cancelled) setCompanySearchResults(result.customers);
      } catch {
        if (!cancelled) setCompanySearchResults([]);
      } finally {
        if (!cancelled) setLoadingCompanySearch(false);
      }
    }, 300);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [companySearchTerm]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [customerData, jobData, stepTypeData] = await Promise.all([
        getRestoreCustomers(),
        getRestoreJobs(),
        getRestoreJobStepTypes(),
      ]);
      setCustomers(customerData);
      setJobs(jobData);
      setStepTypes(stepTypeData);
    } catch (error) {
      setErrorMessage("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const getJobsForCustomer = (customerId: number): RestoreJobResponse[] => {
    return jobs.filter((j) => j.customerId === customerId);
  };

  const companyOptions: IDropdownOption[] = [...databases]
    .sort((a, b) => a.companyLetter.localeCompare(b.companyLetter))
    .map((db) => ({
      key: db.databaseName,
      text: `${db.companyLetter} - ${db.companyName}`,
    }));

  const openAddPanel = () => {
    setCompanyId("");
    setCompanyName("");
    setServer("");
    setDatabases([]);
    setSelectedDatabase("");
    setTargetDatabaseName("");
    setShowConfirmation(false);
    setCompanySearchTerm("");
    setCompanySearchResults([]);
    setErrorMessage("");
    setSuccessMessage("");
    setIsPanelOpen(true);
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case "Completed":
        return { icon: "CompletedSolid", color: "#107C10" };
      case "Failed":
        return { icon: "ErrorBadge", color: "#A4262C" };
      case "In Progress":
        return { icon: "ProgressRingDots", color: "#005A9E" };
      default:
        return { icon: "CircleRing", color: "#c8c6c4" };
    }
  };

  const renderStepTimeline = (steps: RestoreJobStepResponse[]) => {
    if (!steps || steps.length === 0) return null;
    const sorted = [...steps].sort((a, b) => a.sortOrder - b.sortOrder);
    return (
      <div style={{ marginTop: 10, borderTop: "1px solid #edebe9", paddingTop: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#605e5c", marginBottom: 6 }}>Steps</div>
        {sorted.map((step) => {
          const { icon, color } = getStepIcon(step.status);
          return (
            <div key={step.id}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 4 }}>
                <i className={`ms-Icon ms-Icon--${icon}`} style={{ fontSize: 14, color }} />
                <span style={{ flex: 1, color: "#323130" }}>{step.stepTypeName}</span>
                {renderStatusBadge(step.status)}
              </div>
              {step.errorMessage && (
                <div style={{ marginLeft: 22, fontSize: 12, color: "#A4262C", marginBottom: 6 }}>
                  {step.errorMessage}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const handleCreateJob = async () => {
    if (!currentUserEmail) {
      setErrorMessage("Unable to identify current user. Please refresh and try again.");
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setIsSubmitting(true);
    try {
      const db = databases.find((d) => d.databaseName === selectedDatabase);
      // Create the restore customer record — the worker's ExpLicense step adds the entry with the correct prefix
      const customer = await createRestoreCustomer({
        customerId: parseInt(companyId),
        databaseLetter: db?.companyLetter ?? "",
        companyName: db?.companyName ?? companyName,
        sourceDatabase: selectedDatabase,
        targetDatabase: targetDatabaseName,
        server,
        statusId: 2,
        createdBy: currentUserEmail,
      });

      // Create the restore job using step IDs from the API — no hardcoded IDs needed
      await createRestoreJob({
        customerId: customer.id,
        requestedBy: currentUserEmail,
        stepTypeIds: stepTypes.filter((t) => t.category === "Clone").sort((a, b) => a.sortOrder - b.sortOrder).map((t) => t.id),
      });

      setSuccessMessage("Clone job added to queue successfully");
      setShowConfirmation(false);
      setIsPanelOpen(false);
      loadData(); // refresh in background
    } catch (error: any) {
      const detail = error?.response?.data || error?.message || "Unknown error";
      setErrorMessage(`Failed to create restore job: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteCustomer = async () => {
    if (!customerToDelete) return;
    if (!currentUserEmail) {
      setErrorMessage("Unable to identify current user. Please refresh and try again.");
      return;
    }
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await updateRestoreCustomerStatus(customerToDelete.id, 5);
      await createRestoreJob({
        customerId: customerToDelete.id,
        requestedBy: currentUserEmail,
        stepTypeIds: stepTypes.filter((t) => t.category === "Teardown").sort((a, b) => a.sortOrder - b.sortOrder).map((t) => t.id),
      });
      setSuccessMessage(`Teardown job queued for "${customerToDelete.companyName}"`);
      setCustomerToDelete(null);
      setDeleteConfirmName("");
      loadData();
    } catch (error: any) {
      const detail = error?.response?.data || error?.message || "Unknown error";
      setErrorMessage(`Failed to queue teardown: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
    }
  };

  const getStatusStyle = (statusName: string) => {
    switch (statusName) {
      case "Completed":
        return { color: "#107C10", backgroundColor: "#DFF6DD", border: "1px solid #A7D8A5" };
      case "Failed":
        return { color: "#A4262C", backgroundColor: "#FDE7E9", border: "1px solid #F1AEB5" };
      case "In Progress":
        return { color: "#005A9E", backgroundColor: "#DEECF9", border: "1px solid #A0C4E8" };
      case "New":
      default:
        return { color: "#8A8886", backgroundColor: "#F3F2F1", border: "1px solid #D2D0CE" };
    }
  };

  const renderStatusBadge = (statusName: string) => {
    const style = getStatusStyle(statusName);
    return (
      <span
        style={{
          display: "inline-block",
          padding: "2px 8px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 600,
          ...style,
        }}
      >
        {statusName}
      </span>
    );
  };

  const columns: DataGridColumn[] = [
    {
      key: "customerId",
      name: "Company ID",
      fieldName: "customerId",
      minWidth: 80,
      maxWidth: 100,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "companyName",
      name: "Company Name",
      fieldName: "companyName",
      minWidth: 120,
      maxWidth: 200,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "server",
      name: "Server",
      fieldName: "server",
      minWidth: 80,
      maxWidth: 110,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "sourceDatabase",
      name: "Source Database",
      fieldName: "sourceDatabase",
      minWidth: 130,
      maxWidth: 200,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "targetDatabase",
      name: "Target Database",
      fieldName: "targetDatabase",
      minWidth: 130,
      maxWidth: 200,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "statusName",
      name: "Status",
      fieldName: "statusName",
      minWidth: 80,
      maxWidth: 110,
      isResizable: true,
      isPadded: true,
      onRender: (item: any) => renderStatusBadge(item.statusName),
    },
    {
      key: "dateAdded",
      name: "Date Added",
      fieldName: "dateAdded",
      minWidth: 140,
      maxWidth: 180,
      isResizable: true,
      isPadded: true,
      onRender: (item: any) =>
        item.dateAdded ? (
          <span title={item.dateAdded}>
            {new Date(item.dateAdded).toLocaleString()}
          </span>
        ) : (
          <span style={{ color: "#a19f9d" }}>—</span>
        ),
    },
    {
      key: "jobs",
      name: "Jobs",
      fieldName: "",
      minWidth: 60,
      maxWidth: 80,
      isResizable: false,
      isPadded: true,
      onRender: (item: any) => {
        const count = getJobsForCustomer(item.id).length;
        return (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              color: count > 0 ? "#005A9E" : "#a19f9d",
              fontSize: 13,
              fontWeight: count > 0 ? 600 : 400,
              cursor: count > 0 ? "pointer" : "default",
            }}
            onClick={(e) => {
              if (count > 0) {
                e.stopPropagation();
                setSelectedCustomer(item);
                setVisibleJobCount(5);
                setIsDetailPanelOpen(true);
              }
            }}
          >
            <i className="ms-Icon ms-Icon--History" style={{ fontSize: 14 }} />
            {count}
          </span>
        );
      },
    },
    {
      key: "actions",
      name: "",
      fieldName: "",
      minWidth: 40,
      maxWidth: 40,
      isResizable: false,
      isPadded: false,
      onRender: (item: RestoreCustomerResponse) => (
        <div onClick={(e) => e.stopPropagation()}>
          <MoreActionsButton
            menu={[
              {
                key: "delete",
                text: "Delete",
                iconProps: { iconName: "Delete" },
                onClick: () => {
                  setCustomerToDelete(item);
                  setDeleteConfirmName("");
                },
              },
            ]}
          />
        </div>
      ),
    },
  ];

  const filteredCustomers = customers.filter((customer) => {
    if (statusFilter === "Active" && !customer.isActive) return false;
    if (statusFilter === "Inactive" && customer.isActive) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      customer.companyName.toLowerCase().includes(term) ||
      customer.sourceDatabase.toLowerCase().includes(term) ||
      customer.targetDatabase.toLowerCase().includes(term) ||
      customer.server.toLowerCase().includes(term) ||
      customer.customerId.toString().includes(term)
    );
  });

  const renderRow = (
    props?: IDetailsRowProps,
    defaultRender?: (props?: IDetailsRowProps) => JSX.Element | null
  ): JSX.Element | null => {
    if (!props || !defaultRender) return null;

    const item = props.item as RestoreCustomerResponse;
    const customerJobs = getJobsForCustomer(item.id);

    return (
      <div
        onClick={() => {
          if (customerJobs.length > 0) {
            setSelectedCustomer(item);
            setIsDetailPanelOpen(true);
          }
        }}
        style={{ cursor: customerJobs.length > 0 ? "pointer" : "default" }}
      >
        <DetailsRow {...props} />
      </div>
    );
  };

  return (
    <>
      <div className="page-header">
        <h2 className="page-title">Cortex Database Cloning</h2>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <AddButton text="Clone Job" onClick={openAddPanel} />
          <SearchBox
            placeholder="Search customers..."
            onSearch={setSearchTerm}
            disabled={loading}
          />
          <Dropdown
            selectedKey={statusFilter}
            options={[
              { key: "All", text: "All" },
              { key: "Active", text: "Active" },
              { key: "Inactive", text: "Inactive" },
            ]}
            onChange={(_, option) => option && setStatusFilter(option.key as string)}
            styles={{ root: { width: 120 } }}
            disabled={loading}
          />
          <IconButton
            iconProps={{ iconName: "Refresh" }}
            title="Refresh"
            ariaLabel="Refresh"
            onClick={loadData}
            disabled={loading}
            styles={{
              root: { width: 40, height: 40 },
              rootHovered: { backgroundColor: "#f3f2f1" },
              icon: { fontSize: 18, color: loading ? "#a19f9d" : "#0078d4" },
              rootPressed: { backgroundColor: "#e1dfdd" },
              rootDisabled: { backgroundColor: "transparent" },
            }}
          />
        </div>
      </div>

      <div style={{ padding: "0 24px" }}>
        {loading ? (
          <ShimmerList numberOfColumns={8} />
        ) : (
          <DataGrid
            items={filteredCustomers}
            columns={columns}
            onRenderRow={renderRow}
          />
        )}
      </div>

      <Panel
        isOpen={isPanelOpen}
        onDismiss={() => setIsPanelOpen(false)}
        type={PanelType.medium}
        headerText={showConfirmation ? "Confirm Clone" : "Clone Customer Database to Test Cortex"}
      >
        {showConfirmation ? (
          <div style={{ marginTop: "20px" }}>
            <p style={{ fontSize: 14, color: "#323130", marginBottom: 16 }}>
              You are about to add the following database to the clone queue:
            </p>
            <div
              style={{
                backgroundColor: "#faf9f8",
                border: "1px solid #edebe9",
                borderRadius: 4,
                padding: "12px 16px",
                marginBottom: 16,
              }}
            >
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 600, color: "#605e5c" }}>Company: </span>
                <span>{companyName} (ID: {companyId})</span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 600, color: "#605e5c" }}>Server: </span>
                <span>{server}</span>
              </div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 600, color: "#605e5c" }}>Source Database: </span>
                <span>{selectedDatabase}</span>
              </div>
              <div>
                <span style={{ fontWeight: 600, color: "#605e5c" }}>Target Database: </span>
                <span>{targetDatabaseName}</span>
              </div>
            </div>
            <p style={{ fontSize: 13, color: "#605e5c", marginBottom: 20 }}>
              These will be added with an <strong>In Progress</strong> status and processed by the clone service.
            </p>
            <div style={{ display: "flex", gap: "8px" }}>
              <PrimaryButton text="Confirm & Add" onClick={handleCreateJob} disabled={isSubmitting} />
              <DefaultButton text="Back" onClick={() => setShowConfirmation(false)} />
            </div>
          </div>
        ) : (
          <div style={{ marginTop: "20px" }}>
            <div style={{ position: "relative", marginBottom: 16 }}>
              <TextField
                label="Search Company"
                value={companySearchTerm}
                onChange={(_, value) => setCompanySearchTerm(value || "")}
                placeholder="Type company name to search..."
                iconProps={loadingCompanySearch ? { iconName: "ProgressRingDots" } : { iconName: "Search" }}
              />
              {companySearchResults.length > 0 && companySearchTerm.length >= 2 && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    maxHeight: 200,
                    overflowY: "auto",
                    backgroundColor: "#fff",
                    border: "1px solid #edebe9",
                    borderRadius: "0 0 4px 4px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    zIndex: 10,
                  }}
                >
                  {companySearchResults.map((c) => (
                    <div
                      key={`${c.companyId}-${c.serverId}`}
                      onClick={() => {
                        setCompanyId(c.companyId.toString());
                        setCompanyName(c.companyName);
                        setServer(c.serverId ?? "");
                        setCompanySearchTerm("");
                        setCompanySearchResults([]);
                      }}
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        fontSize: 13,
                        borderBottom: "1px solid #f3f2f1",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f3f2f1")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#fff")}
                    >
                      <div style={{ fontWeight: 600, color: "#323130" }}>{c.companyName}</div>
                      <div style={{ fontSize: 12, color: "#605e5c" }}>
                        ID: {c.companyId} — Server: {c.serverId ?? "N/A"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {companyName && (
              <div
                style={{
                  backgroundColor: "#f3f2f1",
                  border: "1px solid #edebe9",
                  borderRadius: 4,
                  padding: "10px 14px",
                  marginBottom: 12,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: "#323130" }}>{companyName}</div>
                  <div style={{ fontSize: 12, color: "#605e5c", marginTop: 2 }}>
                    ID: {companyId} — Server: {server}
                  </div>
                </div>
                <IconButton
                  iconProps={{ iconName: "Cancel" }}
                  title="Clear selection"
                  ariaLabel="Clear selection"
                  styles={{ root: { width: 28, height: 28 }, icon: { fontSize: 12, color: "#605e5c" } }}
                  onClick={() => {
                    setCompanyId("");
                    setCompanyName("");
                    setServer("");
                    setDatabases([]);
                    setSelectedDatabase("");
                    setTargetDatabaseName("");
                  }}
                />
              </div>
            )}
            <Dropdown
              label="Source Database"
              options={companyOptions}
              selectedKey={selectedDatabase}
              onChange={(_, option) => {
                setSelectedDatabase(option?.key as string);
              }}
              required
              disabled={databases.length === 0 || loadingDatabases}
              placeholder={
                loadingDatabases
                  ? "Loading..."
                  : databases.length === 0
                  ? "Search and select a company above"
                  : "Select a source database"
              }
            />
            <TextField
              label="Target Database Name"
              value={targetDatabaseName}
              readOnly
              disabled={!targetDatabaseName}
              placeholder="Auto-filled based on Company ID and existing databases"
            />
            <div style={{ marginTop: "20px", display: "flex", gap: "8px" }}>
              <PrimaryButton
                text="Add to Queue"
                onClick={() => {
                  if (!companyId || !companyName || !server || !selectedDatabase || !targetDatabaseName) {
                    setErrorMessage("All fields are required");
                    return;
                  }
                  setShowConfirmation(true);
                }}
              />
              <DefaultButton text="Cancel" onClick={() => setIsPanelOpen(false)} />
            </div>
          </div>
        )}
      </Panel>

      <Panel
        isOpen={isDetailPanelOpen}
        onDismiss={() => setIsDetailPanelOpen(false)}
        type={PanelType.medium}
        headerText={selectedCustomer ? `${selectedCustomer.companyName} — Clone Jobs` : "Clone Jobs"}
      >
        {selectedCustomer && (
          <div style={{ marginTop: 20 }}>
            {/* Customer summary card */}
            <div
              style={{
                backgroundColor: "#faf9f8",
                border: "1px solid #edebe9",
                borderRadius: 4,
                padding: "12px 16px",
                marginBottom: 24,
              }}
            >
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#8a8886", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Company ID</div>
                  <div style={{ fontSize: 14, color: "#323130" }}>{selectedCustomer.customerId}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#8a8886", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Server</div>
                  <div style={{ fontSize: 14, color: "#323130" }}>{selectedCustomer.server}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#8a8886", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Status</div>
                  <div>{renderStatusBadge(selectedCustomer.statusName)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#8a8886", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Created By</div>
                  <div style={{ fontSize: 14, color: "#323130" }}>
                    {selectedCustomer.createdBy || "—"}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginTop: 12 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#8a8886", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Source</div>
                  <div style={{ fontSize: 13, color: "#323130", fontFamily: "monospace" }}>{selectedCustomer.sourceDatabase}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#8a8886", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 2 }}>Target</div>
                  <div style={{ fontSize: 13, color: "#323130", fontFamily: "monospace" }}>{selectedCustomer.targetDatabase}</div>
                </div>
              </div>
            </div>

            {/* Restore Again / Retry Failed Steps actions */}
            <div style={{ marginBottom: 20, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {(() => {
                const customerJobs = getJobsForCustomer(selectedCustomer.id);
                const latestJob = customerJobs.length > 0
                  ? [...customerJobs].sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime())[0]
                  : null;
                const hasFailed = latestJob?.status === "Failed";
                const incompleteStepTypeIds = hasFailed && latestJob?.steps
                  ? latestJob.steps
                      .filter((s) => s.status !== "Completed")
                      .map((s) => stepTypes.find((t) => t.name === s.stepTypeName)?.id ?? 0)
                      .filter((id) => id > 0)
                  : [];

                return (
                  <>
                    {hasFailed && incompleteStepTypeIds.length > 0 && (
                      <PrimaryButton
                        text="Retry Failed Steps"
                        iconProps={{ iconName: "Refresh" }}
                        onClick={async () => {
                          try {
                            const failedSteps = latestJob!.steps.filter((s) => s.status !== "Completed");

                            // Reset all non-completed steps to New concurrently
                            await Promise.all(failedSteps.map((step) => updateRestoreJobStep(latestJob!.id, step.id, 1)));

                            setSuccessMessage("Failed steps reset — worker will retry automatically");
                            loadData();
                          } catch (error: any) {
                            const detail = error?.response?.data || error?.message || "Unknown error";
                            setErrorMessage(`Failed to retry: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
                          }
                        }}
                      />
                    )}
                    <PrimaryButton
                      text="Restore Again"
                      iconProps={{ iconName: "Sync" }}
                      disabled={selectedCustomer.statusId !== 3}
                      onClick={async () => {
                        if (!currentUserEmail) {
                          setErrorMessage("Unable to identify current user. Please refresh and try again.");
                          return;
                        }
                        setErrorMessage("");
                        setSuccessMessage("");
                        try {
                          await createRestoreJob({
                            customerId: selectedCustomer.id,
                            requestedBy: currentUserEmail,
                            stepTypeIds: stepTypes
                              .filter((t) => t.name === "Database Restore" || t.name === "FrostByte Sync")
                              .sort((a, b) => a.sortOrder - b.sortOrder)
                              .map((t) => t.id),
                          });
                          setSuccessMessage("New restore job created successfully");
                          loadData();
                        } catch (error: any) {
                          const detail = error?.response?.data || error?.message || "Unknown error";
                          setErrorMessage(`Failed to create restore job: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
                        }
                      }}
                    />
                  </>
                );
              })()}
              {selectedCustomer.statusId !== 3 && !getJobsForCustomer(selectedCustomer.id).some((j) => j.status === "Failed") && (
                <div style={{ fontSize: 12, color: "#8a8886", marginTop: 6, width: "100%" }}>
                  A new restore can only be started when the current status is Completed.
                </div>
              )}
            </div>

            {/* Job timeline */}
            {(() => {
              const allJobs = getJobsForCustomer(selectedCustomer.id);
              const sortedJobs = [...allJobs].sort(
                (a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
              );
              const initialJobId = sortedJobs.length > 0
                ? sortedJobs.reduce((oldest, j) =>
                    new Date(j.requestedAt) < new Date(oldest.requestedAt) ? j : oldest
                  ).id
                : -1;
              const visibleJobs = sortedJobs.slice(0, visibleJobCount);
              const remaining = sortedJobs.length - visibleJobCount;

              return (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "#323130" }}>
                      Restore Jobs ({allJobs.length})
                    </div>
                    {visibleJobCount < sortedJobs.length && (
                      <span
                        style={{ fontSize: 12, color: "#005A9E", cursor: "pointer" }}
                        onClick={() => setVisibleJobCount(sortedJobs.length)}
                      >
                        Show All
                      </span>
                    )}
                    {visibleJobCount >= sortedJobs.length && sortedJobs.length > 5 && (
                      <span
                        style={{ fontSize: 12, color: "#005A9E", cursor: "pointer" }}
                        onClick={() => setVisibleJobCount(5)}
                      >
                        Show Less
                      </span>
                    )}
                  </div>
                  {visibleJobs.map((job) => {
                    const isInitial = job.id === initialJobId;
                    const teardownStepNames = stepTypes.filter((t) => t.category === "Teardown").map((t) => t.name);
                    const isTeardown = job.steps?.some((s) => teardownStepNames.includes(s.stepTypeName));
                    return isTeardown ? (
                      <div
                        key={job.id}
                        style={{
                          borderLeft: "3px solid #A4262C",
                          borderTop: "1px solid #f1aeb5",
                          borderRight: "1px solid #f1aeb5",
                          borderBottom: "1px solid #f1aeb5",
                          borderRadius: "0 6px 6px 0",
                          padding: "10px 14px",
                          marginBottom: 8,
                          backgroundColor: "#fdf4f4",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <i className="ms-Icon ms-Icon--Delete" style={{ fontSize: 12, color: "#A4262C" }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#A4262C" }}>Teardown</span>
                          </div>
                          {renderStatusBadge(job.status)}
                        </div>
                        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#605e5c" }}>
                          <span>
                            <i className="ms-Icon ms-Icon--Contact" style={{ fontSize: 12, marginRight: 4 }} />
                            {job.requestedBy || "—"}
                          </span>
                          <span>
                            <i className="ms-Icon ms-Icon--Clock" style={{ fontSize: 12, marginRight: 4 }} />
                            {job.requestedAt ? new Date(job.requestedAt).toLocaleString() : "—"}
                          </span>
                          {job.completedOn && (
                            <span>
                              <i className="ms-Icon ms-Icon--CompletedSolid" style={{ fontSize: 12, marginRight: 4, color: "#107C10" }} />
                              {new Date(job.completedOn).toLocaleString()}
                            </span>
                          )}
                        </div>
                        {renderStepTimeline(job.steps)}
                      </div>
                    ) : isInitial ? (
                      <div
                        key={job.id}
                        style={{
                          border: "1px solid #edebe9",
                          borderRadius: 6,
                          padding: "14px 16px",
                          marginBottom: 10,
                          backgroundColor: "#fff",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#323130" }}>Initial Clone</span>
                            <span style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: "#5c2d91",
                              backgroundColor: "#f0e6ff",
                              border: "1px solid #d4a8ff",
                              borderRadius: 10,
                              padding: "1px 7px",
                            }}>
                              Full Setup
                            </span>
                          </div>
                          {renderStatusBadge(job.status)}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                            <i className="ms-Icon ms-Icon--Contact" style={{ fontSize: 14, color: "#605e5c" }} />
                            <span style={{ color: "#605e5c", minWidth: 70 }}>By</span>
                            <span style={{ color: job.requestedBy ? "#323130" : "#a19f9d" }}>{job.requestedBy || "—"}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                            <i className="ms-Icon ms-Icon--Clock" style={{ fontSize: 14, color: "#605e5c" }} />
                            <span style={{ color: "#605e5c", minWidth: 70 }}>Requested</span>
                            <span style={{ color: "#323130" }}>{job.requestedAt ? new Date(job.requestedAt).toLocaleString() : "—"}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                            <i className="ms-Icon ms-Icon--Play" style={{ fontSize: 14, color: job.startedOn ? "#005A9E" : "#c8c6c4" }} />
                            <span style={{ color: "#605e5c", minWidth: 70 }}>Started</span>
                            <span style={{ color: job.startedOn ? "#323130" : "#a19f9d" }}>{job.startedOn ? new Date(job.startedOn).toLocaleString() : "—"}</span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                            <i className="ms-Icon ms-Icon--CompletedSolid" style={{ fontSize: 14, color: job.completedOn ? "#107C10" : "#c8c6c4" }} />
                            <span style={{ color: "#605e5c", minWidth: 70 }}>Completed</span>
                            <span style={{ color: job.completedOn ? "#323130" : "#a19f9d" }}>{job.completedOn ? new Date(job.completedOn).toLocaleString() : "—"}</span>
                          </div>
                        </div>
                        {renderStepTimeline(job.steps)}
                      </div>
                    ) : (
                      <div
                        key={job.id}
                        style={{
                          borderLeft: "3px solid #0078d4",
                          borderTop: "1px solid #deecf9",
                          borderRight: "1px solid #deecf9",
                          borderBottom: "1px solid #deecf9",
                          borderRadius: "0 6px 6px 0",
                          padding: "10px 14px",
                          marginBottom: 8,
                          backgroundColor: "#f8fbff",
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <i className="ms-Icon ms-Icon--Sync" style={{ fontSize: 12, color: "#0078d4" }} />
                            <span style={{ fontSize: 13, fontWeight: 600, color: "#0078d4" }}>Database Re-run</span>
                          </div>
                          {renderStatusBadge(job.status)}
                        </div>
                        <div style={{ display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "#605e5c" }}>
                          <span>
                            <i className="ms-Icon ms-Icon--Contact" style={{ fontSize: 12, marginRight: 4 }} />
                            {job.requestedBy || "—"}
                          </span>
                          <span>
                            <i className="ms-Icon ms-Icon--Clock" style={{ fontSize: 12, marginRight: 4 }} />
                            {job.requestedAt ? new Date(job.requestedAt).toLocaleString() : "—"}
                          </span>
                          {job.completedOn && (
                            <span>
                              <i className="ms-Icon ms-Icon--CompletedSolid" style={{ fontSize: 12, marginRight: 4, color: "#107C10" }} />
                              {new Date(job.completedOn).toLocaleString()}
                            </span>
                          )}
                        </div>
                        {job.steps?.some((s) => s.status === "Failed") && renderStepTimeline(job.steps)}
                      </div>
                    );
                  })}
                  {remaining > 0 && (
                    <div
                      onClick={() => setVisibleJobCount((prev) => prev + 5)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 6,
                        padding: "10px 0",
                        cursor: "pointer",
                        color: "#005A9E",
                        fontSize: 13,
                        fontWeight: 500,
                        borderTop: "1px solid #edebe9",
                        marginTop: 4,
                      }}
                    >
                      <i className="ms-Icon ms-Icon--ChevronDown" style={{ fontSize: 12 }} />
                      Show More ({remaining} remaining)
                    </div>
                  )}
                  {allJobs.length === 0 && (
                    <div style={{ color: "#a19f9d", fontSize: 13, fontStyle: "italic" }}>
                      No restore jobs found for this customer.
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}
      </Panel>

      <Panel
        isOpen={!!customerToDelete}
        onDismiss={() => { setCustomerToDelete(null); setDeleteConfirmName(""); }}
        type={PanelType.small}
        headerText="Teardown Clone Entry"
      >
        {customerToDelete && (
          <div style={{ marginTop: 20 }}>
            <p style={{ fontSize: 14, color: "#323130", marginBottom: 12 }}>
              This will queue a teardown job to reverse the initial setup for:
            </p>
            <div
              style={{
                backgroundColor: "#fdf4f4",
                border: "1px solid #f1aeb5",
                borderRadius: 4,
                padding: "10px 14px",
                marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, color: "#323130" }}>{customerToDelete.companyName}</div>
              <div style={{ fontSize: 12, color: "#605e5c", marginTop: 2 }}>
                ID: {customerToDelete.customerId} &mdash; {customerToDelete.server}
              </div>
            </div>
            <p style={{ fontSize: 13, color: "#605e5c", marginBottom: 4 }}>
              Type the company name to confirm:
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{customerToDelete.companyName}</span>
              <IconButton
                iconProps={{ iconName: "Copy" }}
                title="Copy company name"
                ariaLabel="Copy company name"
                styles={{ root: { width: 28, height: 28 }, icon: { fontSize: 13 } }}
                onClick={() => navigator.clipboard.writeText(customerToDelete.companyName)}
              />
            </div>
            <TextField
              value={deleteConfirmName}
              onChange={(_, val) => setDeleteConfirmName(val || "")}
              placeholder="Type company name here"
            />
            <div style={{ marginTop: 20, display: "flex", gap: 8 }}>
              <PrimaryButton
                text="Teardown"
                disabled={deleteConfirmName !== customerToDelete.companyName}
                styles={{
                  root: { backgroundColor: "#A4262C", border: "none" },
                  rootHovered: { backgroundColor: "#7B1A1E", border: "none" },
                  rootDisabled: { backgroundColor: "#f3d0d0", border: "none" },
                }}
                onClick={handleDeleteCustomer}
              />
              <DefaultButton
                text="Cancel"
                onClick={() => { setCustomerToDelete(null); setDeleteConfirmName(""); }}
              />
            </div>
          </div>
        )}
      </Panel>

      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          onDismiss={() => setErrorMessage("")}
          styles={{ root: { position: "fixed", bottom: 0, left: 0, width: "100%", zIndex: 1000 } }}
        >
          {errorMessage}
        </MessageBar>
      )}
      {successMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          onDismiss={() => setSuccessMessage("")}
          styles={{ root: { position: "fixed", bottom: 0, left: 0, width: "100%", zIndex: 1000 } }}
        >
          {successMessage}
        </MessageBar>
      )}
    </>
  );
};

export default CortexCloningPage;
